import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Eye, EyeOff, Sun, Moon, MonitorSmartphone, Fingerprint, Loader2, Key } from "lucide-react";
import { useTheme } from "@/hooks/useTheme";
import { toast } from "sonner";
import axios from "axios";

// WebAuthn helpers
const base64urlToBuffer = (base64url) => {
  const padding = "=".repeat((4 - (base64url.length % 4)) % 4);
  const base64 = (base64url + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray.buffer;
};

const bufferToBase64url = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let str = "";
  for (const byte of bytes) {
    str += String.fromCharCode(byte);
  }
  return window.btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
};

const isWebAuthnSupported = () => window.PublicKeyCredential !== undefined;

function ThemeToggle() {
  const { theme, setTheme } = useTheme();
  const cycle = () => {
    const order = ["system", "light", "dark"];
    setTheme(order[(order.indexOf(theme) + 1) % order.length]);
  };
  const Icon = theme === "dark" ? Moon : theme === "light" ? Sun : MonitorSmartphone;
  const label = theme === "dark" ? "Dark" : theme === "light" ? "Light" : "Auto";
  return (
    <button onClick={cycle} className="nl-theme-toggle font-mono" data-testid="theme-toggle" title={`Theme: ${label}`}>
      <Icon size={14} /> {label}
    </button>
  );
}

function LiveClock() {
  const [time, setTime] = useState("");
  useEffect(() => {
    const tick = () => setTime(`UTC ${new Date().toISOString().slice(11, 19)}`);
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);
  return <span className="nl-clock">{time}</span>;
}

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [hasPasskey, setHasPasskey] = useState(false);
  const [error, setError] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Recovery modal state
  const [showRecoveryModal, setShowRecoveryModal] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState("");
  const [recoveryCode, setRecoveryCode] = useState("");
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const [recoveryError, setRecoveryError] = useState("");

  // Check if session expired
  const sessionExpired = searchParams.get("session") === "expired";

  useEffect(() => {
    // Show toast if session expired
    if (sessionExpired) {
      toast.error("Your session has expired. Please log in again.", {
        duration: 5000,
      });
    }
  }, [sessionExpired]);

  useEffect(() => {
    // P0 Security: Exchange SSO code for token (tokens never in URLs)
    const ssoCode = searchParams.get("sso_code");
    if (ssoCode) {
      setSsoLoading(true);
      axios.post(`${API}/auth/sso/exchange?code=${ssoCode}`)
        .then(res => {
          localStorage.setItem("token", res.data.access_token);
          toast.success(`Welcome, ${res.data.name || "User"}!`);
          window.location.href = "/";
        })
        .catch(err => {
          setError(err.response?.data?.detail || "SSO exchange failed");
          setSsoLoading(false);
        });
      return;
    }
    
    // Legacy fallback for old sso_token param (remove after migration)
    const ssoToken = searchParams.get("sso_token");
    const ssoName = searchParams.get("sso_name");
    if (ssoToken) {
      localStorage.setItem("token", ssoToken);
      toast.success(`Welcome, ${ssoName || "User"}!`);
      window.location.href = "/";
    }
    const ssoErr = searchParams.get("sso_error");
    if (ssoErr) setError(decodeURIComponent(ssoErr));
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !password) { setError("Email and password are required."); return; }
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      navigate("/");
    } catch (err) {
      setError(err.response?.data?.detail || "Authentication failed.");
    } finally { setLoading(false); }
  };

  const handleSSO = async () => {
    setSsoLoading(true); setError("");
    try {
      const res = await axios.post(`${API}/sso/login`);
      if (res.data.status === "redirect" && res.data.auth_url) {
        toast.info("Redirecting to Microsoft...");
        window.location.href = res.data.auth_url;
        return;
      }
      toast.warning(res.data.message || "SSO not configured.");
    } catch (err) {
      setError(`SSO error: ${err.response?.data?.detail || err.message}`);
    }
    setSsoLoading(false);
  };

  // Check if email has PassKey registered
  const checkPasskey = async (emailToCheck) => {
    if (!emailToCheck || !isWebAuthnSupported()) return;
    try {
      const res = await axios.get(`${API}/auth/webauthn/check/${encodeURIComponent(emailToCheck)}`);
      setHasPasskey(res.data.has_passkey);
    } catch {
      setHasPasskey(false);
    }
  };

  // Debounce passkey check on email change
  useEffect(() => {
    const timer = setTimeout(() => {
      if (email && email.includes("@")) {
        checkPasskey(email);
      } else {
        setHasPasskey(false);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [email]);

  // Handle PassKey login
  const handlePasskeyLogin = async () => {
    if (!isWebAuthnSupported()) {
      toast.error("Your browser doesn't support PassKeys");
      return;
    }

    setPasskeyLoading(true);
    setError("");

    try {
      // Step 1: Get authentication options
      const optionsRes = await axios.post(`${API}/auth/webauthn/login/options`, {
        email: email || null,
      });

      const options = optionsRes.data.options;
      const challenge = optionsRes.data.challenge;

      // Convert base64url to ArrayBuffer
      options.challenge = base64urlToBuffer(options.challenge);
      
      if (options.allowCredentials) {
        options.allowCredentials = options.allowCredentials.map((cred) => ({
          ...cred,
          id: base64urlToBuffer(cred.id),
        }));
      }

      // Step 2: Get credential from browser
      const credential = await navigator.credentials.get({
        publicKey: options,
      });

      // Step 3: Prepare credential for server
      const credentialForServer = {
        id: credential.id,
        rawId: bufferToBase64url(credential.rawId),
        type: credential.type,
        response: {
          clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
          authenticatorData: bufferToBase64url(credential.response.authenticatorData),
          signature: bufferToBase64url(credential.response.signature),
          userHandle: credential.response.userHandle 
            ? bufferToBase64url(credential.response.userHandle) 
            : null,
        },
      };

      // Step 4: Verify with server and get tokens
      const verifyRes = await axios.post(`${API}/auth/webauthn/login/verify`, {
        credential: credentialForServer,
        challenge: challenge,
      });

      // Step 5: Store token and redirect
      localStorage.setItem("token", verifyRes.data.access_token);
      if (verifyRes.data.refresh_token) {
        localStorage.setItem("refresh_token", verifyRes.data.refresh_token);
      }

      toast.success(`Welcome back, ${verifyRes.data.user?.name || verifyRes.data.user?.email || "User"}!`);
      window.location.href = "/";

    } catch (err) {
      console.error("PassKey login failed:", err);
      if (err.name === "NotAllowedError") {
        setError("PassKey authentication cancelled or timed out");
      } else if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else {
        setError("PassKey authentication failed");
      }
    } finally {
      setPasskeyLoading(false);
    }
  };

  // Handle account recovery with recovery code
  const handleRecoverySubmit = async (e) => {
    e.preventDefault();
    setRecoveryError("");
    
    if (!recoveryEmail || !recoveryCode) {
      setRecoveryError("Email and recovery code are required");
      return;
    }
    
    setRecoveryLoading(true);
    try {
      const res = await axios.post(`${API}/auth/webauthn/recovery/verify`, {
        email: recoveryEmail,
        recovery_code: recoveryCode,
      });
      
      // Store tokens
      localStorage.setItem("token", res.data.access_token);
      if (res.data.refresh_token) {
        localStorage.setItem("refresh_token", res.data.refresh_token);
      }
      
      toast.success("Recovery successful! Please register a new PassKey.");
      
      // Close modal and redirect
      setShowRecoveryModal(false);
      window.location.href = "/";
      
    } catch (err) {
      setRecoveryError(err.response?.data?.detail || "Invalid recovery code or email");
    } finally {
      setRecoveryLoading(false);
    }
  };

  return (
    <div className="nl-page" data-testid="login-page">
      <div className="nl-grid" aria-hidden="true" />
      <div className="nl-scan" aria-hidden="true" />

      {/* Left branding panel */}
      <div className="nl-left">
        <div className="nl-brand">
          <div className="nl-logo-mark" />
          <div>
            <div className="nl-logo-name font-heading">Niyant</div>
            <div className="nl-logo-ver font-mono">v2.4 · production</div>
          </div>
        </div>

        <div className="nl-hero">
          <p className="nl-hero-tag font-mono">SRE · DevOps · CloudOps</p>
          <h1 className="nl-hero-h1 font-heading" data-testid="login-title">
            Stop<br />Backlog.<br /><span className="nl-accent-text">Go Build.</span>
          </h1>
          <div className="nl-hero-desc font-mono">
            Your cloud estate, fully mapped.<br />
            <strong>Discover. Relate. Act.</strong><br />
            Hybrid-native. Zero guesswork.
          </div>
        </div>

        <div className="nl-status font-mono">
          <span className="nl-status-item"><span className="nl-dot" />All systems operational</span>
          <LiveClock />
        </div>
      </div>

      {/* Right sign-in panel */}
      <div className="nl-right">
        <ThemeToggle />
        <div className="nl-form-wrap">
          <p className="nl-form-tag font-mono">// access terminal</p>
          <h2 className="nl-form-title font-heading">Sign in</h2>
          <p className="nl-form-sub font-mono">Authenticated session · TLS 1.3</p>

          {error && <div className="nl-error font-mono" data-testid="login-error">{error}</div>}

          <form onSubmit={handleSubmit} className="nl-form">
            <div className="nl-field">
              <label className="nl-label font-mono">Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="you@yourorg.com" autoComplete="email"
                className="nl-input font-mono" data-testid="login-email-input" />
            </div>

            <div className="nl-field">
              <label className="nl-label font-mono">Password</label>
              <div className="nl-pw-wrap">
                <input type={showPassword ? "text" : "password"} value={password}
                  onChange={e => setPassword(e.target.value)} placeholder="••••••••••••"
                  autoComplete="current-password" className="nl-input font-mono"
                  data-testid="login-password-input" />
                <button type="button" className="nl-pw-toggle font-mono"
                  onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>

            <div className="nl-meta">
              <Link to="/reset-password" className="nl-forgot font-mono" data-testid="forgot-password-link">
                Reset access &rarr;
              </Link>
            </div>

            <button type="submit" disabled={loading}
              className={`nl-btn-primary${loading ? " nl-loading" : ""}`}
              data-testid="login-submit-button">
              <span>{loading ? "" : "Sign In"}</span>
            </button>
          </form>

          <div className="nl-divider"><div className="nl-div-line" /><span className="nl-div-text font-mono">or</span><div className="nl-div-line" /></div>

          {/* PassKey Login Button */}
          {isWebAuthnSupported() && (
            <button 
              type="button" 
              className={`nl-btn-passkey font-mono ${hasPasskey ? "nl-passkey-available" : ""}`}
              onClick={handlePasskeyLogin}
              disabled={passkeyLoading}
              data-testid="passkey-login-button"
            >
              {passkeyLoading ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Fingerprint size={16} />
              )}
              {passkeyLoading ? "Authenticating..." : hasPasskey ? "Sign in with PassKey" : "Use PassKey"}
            </button>
          )}

          {/* Account Recovery Link */}
          {isWebAuthnSupported() && (
            <button
              type="button"
              className="nl-recovery-link font-mono"
              onClick={() => {
                setShowRecoveryModal(true);
                setRecoveryEmail(email);
                setRecoveryError("");
              }}
              data-testid="account-recovery-link"
            >
              <Key size={12} />
              Lost your PassKey? Use recovery code
            </button>
          )}

          <button type="button" className="nl-btn-sso font-mono" onClick={handleSSO}
            disabled={ssoLoading} data-testid="sso-login-button">
            <svg width="16" height="16" viewBox="0 0 21 21" fill="none"><path d="M10.5 0C4.7 0 0 4.7 0 10.5S4.7 21 10.5 21 21 16.3 21 10.5 16.3 0 10.5 0z" fill="#0078D4"/><rect x="4" y="4" width="5.5" height="5.5" fill="white"/><rect x="11" y="4" width="5.5" height="5.5" fill="white" opacity=".7"/><rect x="4" y="11" width="5.5" height="5.5" fill="white" opacity=".7"/><rect x="11" y="11" width="5.5" height="5.5" fill="white" opacity=".4"/></svg>
            {ssoLoading ? "Connecting..." : "Continue with Microsoft SSO"}
          </button>

          <div className="nl-footer">
            <span className="nl-footer-text font-mono">&copy; 2026 Niyant · Go Build</span>
            <span className="nl-motto font-mono">Go Build</span>
          </div>

          <p className="nl-register font-mono">
            Don't have an account? <Link to="/register" data-testid="register-link">Sign up</Link>
          </p>
        </div>
      </div>

      {/* Recovery Code Modal */}
      {showRecoveryModal && (
        <div className="nl-modal-overlay" onClick={() => setShowRecoveryModal(false)}>
          <div 
            className="nl-modal" 
            onClick={(e) => e.stopPropagation()}
            data-testid="recovery-modal"
          >
            <div className="nl-modal-header">
              <Key size={20} className="text-[var(--hig-accent)]" />
              <div>
                <h3 className="nl-modal-title font-heading">Account Recovery</h3>
                <p className="nl-modal-desc font-mono">Enter your email and recovery code</p>
              </div>
            </div>

            {recoveryError && (
              <div className="nl-error font-mono">{recoveryError}</div>
            )}

            <form onSubmit={handleRecoverySubmit} className="nl-form">
              <div className="nl-field">
                <label className="nl-label font-mono">Email</label>
                <input 
                  type="email" 
                  value={recoveryEmail} 
                  onChange={(e) => setRecoveryEmail(e.target.value)}
                  placeholder="you@yourorg.com"
                  className="nl-input font-mono"
                  data-testid="recovery-email-input"
                />
              </div>

              <div className="nl-field">
                <label className="nl-label font-mono">Recovery Code</label>
                <input 
                  type="text" 
                  value={recoveryCode} 
                  onChange={(e) => setRecoveryCode(e.target.value.toUpperCase())}
                  placeholder="XXXX-XXXX-XXXX"
                  className="nl-input font-mono"
                  style={{ letterSpacing: "0.1em" }}
                  data-testid="recovery-code-input"
                />
                <p className="nl-field-hint font-mono">Enter one of your 12-character recovery codes</p>
              </div>

              <div className="nl-modal-actions">
                <button 
                  type="button" 
                  className="nl-btn-secondary font-mono"
                  onClick={() => setShowRecoveryModal(false)}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className={`nl-btn-primary${recoveryLoading ? " nl-loading" : ""}`}
                  disabled={recoveryLoading}
                  data-testid="recovery-submit-button"
                >
                  <span>{recoveryLoading ? "" : "Recover Account"}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
